import React, { useEffect, useState } from "react";
import { Sparkles, CheckCircle2, Circle, ArrowRight, Trophy } from "lucide-react";
import api from "@/lib/api";
import { BRANCH_META } from "@/lib/branches";

/**
 * Cross-Discipline Career Path card — shown to authenticated users whose
 * beacons cluster across two different disciplines. Renders exactly like
 * CareerPathCard but with a distinct visual (dual-branch gradient) and
 * calls the /me/career-paths/cross-discipline endpoint. Hides gracefully
 * for users who don't yet have 2+ beacons.
 */
export default function CrossDisciplinePathCard() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        api.get("/me/career-paths/cross-discipline")
            .then((r) => setData(r.data))
            .catch(() => setHidden(true));
    }, []);

    if (hidden) return null;
    if (!data) return null;
    if (!data.eligible) return null;

    const start = async () => {
        if (!data.path?.slug || busy) return;
        setBusy(true);
        try {
            await api.post(`/career-paths/${data.path.slug}/start`);
            const { data: fresh } = await api.get("/me/career-paths/cross-discipline");
            setData(fresh);
        } catch {
            /* noop */
        } finally {
            setBusy(false);
        }
    };

    const { path, progress, started, disciplines } = data;
    const [discA, discB] = disciplines;
    const metaA = BRANCH_META[discA.branch] || BRANCH_META.arts;
    const metaB = BRANCH_META[discB.branch] || BRANCH_META.arts;
    const isStarted = !!started;
    const isCompleted = !!(started && started.completed_at);
    const pct = progress?.pct ?? 0;

    return (
        <div
            className="rounded-3xl p-6 sm:p-7 relative overflow-hidden shadow-lg"
            style={{
                background: `linear-gradient(120deg, ${metaA.soft} 0%, #FDFBF7 50%, ${metaB.soft} 100%)`,
                borderTop: `4px solid transparent`,
                borderImage: `linear-gradient(90deg, ${metaA.color}, ${metaB.color}) 1`,
            }}
            data-testid="cross-discipline-card"
        >
            <div className="absolute top-6 right-6 opacity-10 pointer-events-none">
                <Sparkles className="w-24 h-24" color={metaA.color} />
            </div>

            <div className="relative">
                {/* Header with a dual-color monogram */}
                <div className="flex items-start gap-3">
                    <div className="flex flex-shrink-0">
                        <div
                            className="w-12 h-12 rounded-2xl flex items-center justify-center font-display font-black text-white text-lg"
                            style={{
                                background: `linear-gradient(135deg, ${metaA.color}, ${metaB.color})`,
                                boxShadow: `0 4px 20px ${metaA.color}44`,
                            }}
                            data-testid="cross-discipline-org-mark"
                        >
                            {(path.org_name || "?")
                                .split(" ")
                                .slice(0, 2)
                                .map((w) => w[0])
                                .join("")}
                        </div>
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: metaA.text }}>
                            <Sparkles className="w-3 h-3" />
                            Cross-discipline · {discA.name} × {discB.name}
                        </div>
                        <div
                            className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-slate-900 leading-tight"
                            data-testid="cross-discipline-org-name"
                        >
                            {path.org_name}
                        </div>
                        <div className="text-sm text-slate-700 mt-0.5">
                            Track: <span className="font-bold">{path.role_title}</span>
                        </div>
                    </div>
                    {isCompleted && (
                        <div
                            className="chip flex-shrink-0"
                            style={{ background: `linear-gradient(135deg, ${metaA.color}, ${metaB.color})`, color: "#FFF" }}
                        >
                            <Trophy className="w-3 h-3" /> Completed
                        </div>
                    )}
                </div>

                {/* Mission + intro */}
                {path.mission && (
                    <p className="text-sm text-slate-700 mt-4 leading-relaxed">
                        {path.mission}
                    </p>
                )}
                {path.intro && (
                    <p className="text-sm text-slate-800 mt-3 italic border-l-2 pl-3" style={{ borderColor: metaA.color }}>
                        {path.intro}
                    </p>
                )}

                {/* Progress bar */}
                {progress && (
                    <div className="mt-5" data-testid="cross-discipline-progress">
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-display font-bold text-slate-700">
                                {progress.done_steps}/{progress.total_steps} steps
                            </span>
                            <span className="font-mono text-slate-500">{pct}%</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-white/80 overflow-hidden">
                            <div
                                className="h-full transition-all duration-500 ease-out"
                                style={{
                                    width: `${pct}%`,
                                    background: `linear-gradient(90deg, ${metaA.color}, ${metaB.color})`,
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Roadmap */}
                <ol className="mt-5 space-y-2">
                    {(progress?.steps || path.roadmap || []).map((step, i) => {
                        const done = !!step.done;
                        return (
                            <li key={i} className="flex items-start gap-3 text-sm">
                                {done ? (
                                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" color={metaA.color} />
                                ) : (
                                    <Circle className="w-5 h-5 flex-shrink-0 mt-0.5 text-slate-300" />
                                )}
                                <div className="min-w-0">
                                    <div className={done ? "text-slate-900 font-display font-bold line-through decoration-2 opacity-70" : "text-slate-900 font-display font-bold"}>
                                        {step.title}
                                    </div>
                                    {step.current !== undefined && (
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {step.current} / {step.target}
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>

                {/* CTA */}
                <div className="mt-5 flex flex-wrap gap-2 items-center">
                    {!isStarted && !isCompleted && (
                        <button
                            onClick={start}
                            disabled={busy}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="cross-discipline-start"
                        >
                            {busy ? "Starting…" : "Start this path"} <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                    {isStarted && !isCompleted && (
                        <span className="chip bg-slate-900/5 text-slate-700">In progress</span>
                    )}
                    {isCompleted && (
                        <span className="text-xs text-slate-600">This path is stamped on your resume.</span>
                    )}
                </div>
            </div>
        </div>
    );
}
